import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { PulseLoader } from "react-spinners";
import SubDepartmentLayout from "./subdepartment-layout";
import { GetAllDepartmentsForCreateScreen } from "../../../../services/api/department-api";
import { GetAllProjectTemplates } from "../../../../services/api/project-template-api";
import { GetTaskTypesForTask } from "../../../../services/api/task-types-api";
import { CreateSubDepartment } from "../../../../services/api/subdepartment-api";
import {
  successNotification,
  errorNotification,
} from "../../../../redux/notifications";
import { validateForm } from "./subdepartment-util";

const AdminSubDepartmentCreatePanel = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [selectedValue, setSelectedValue] = useState("General");
  const [subDepartment, setSubDepartment] = useState({
    Name: "",
    Departments: [],
    Active: true,
    DefaultProjectTemplate: {},
    DefaultTaskType: {},
    allDepartments: [],
    allProjectTemplates: [],
    allTaskTypes: [],
    WidgetPairs: [],
    ObjectLabels: {
      ProjectSingular: "Project",
      ProjectPlural: "Projects",
      ProjectSingularPossessive: "Project's",
      ProjectPluralPossessive: "Projects'",
      TaskSingular: "Task",
      TaskPlural: "Tasks",
      TaskSingularPossessive: "Task's",
      TaskPluralPossessive: "Tasks'",
      FormSingular: "Form",
      FormPlural: "Forms",
      FormSingularPossessive: "Form's",
      FormPluralPossessive: "Forms'",
    },
  });
  const [errors, setErrors] = useState({
    Name: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const searchQuery = {
          pageSize: 100000,
          pageNumber: 1,
          sortDesc: false,
          sortBy: "name",
          filters: [],
        };
        const [
          allDepartmentsResponse,
          allProjectTemplatesResponse,
          taskTypesResponse,
        ] = await Promise.all([
          GetAllDepartmentsForCreateScreen(searchQuery),
          GetAllProjectTemplates(),
          GetTaskTypesForTask(),
        ]);
        const activeDepartments = allDepartmentsResponse?.data?.dataList
          .filter((department) => department.Active && department.Active === true)
          .sort((a, b) => a?.Name?.localeCompare(b.Name)) ?? [];
        setSubDepartment((prevState) => ({
          ...prevState,
          allDepartments: activeDepartments,
          allProjectTemplates: allProjectTemplatesResponse?.data?.sort((a, b) => a?.ProjectType?.localeCompare(b.ProjectType)),
          allTaskTypes: taskTypesResponse?.data?.sort((a, b) => a?.Type?.localeCompare(b.Type)),
        }));
      } catch (error) {
        dispatch(
          errorNotification({
            header: "Error",
            message: "Failed to fetch initial data.",
          })
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleSave = async () => {
    if (!validateForm(subDepartment, setErrors, setSelectedValue)) {
      return;
    }
    const widgetPairsCopy = [...subDepartment.WidgetPairs];

    const lastPair = widgetPairsCopy[widgetPairsCopy.length - 1];
    if (lastPair && !lastPair.WidgetProjectTemplate?.Id && !lastPair.WidgetTaskType?.Id) {
      widgetPairsCopy.pop();
    }

    const subDepartmentRequest = {
      Name: subDepartment.Name,
      Active: subDepartment.Active,
      Departments: subDepartment.Departments,
      DefaultProjectTemplate: subDepartment.DefaultProjectTemplate,
      DefaultTaskType: subDepartment.DefaultTaskType,
      DefaultStartPage: subDepartment.DefaultStartPage,
      WidgetPairs: widgetPairsCopy,
      ObjectLabels: subDepartment.ObjectLabels,
    };

    setIsSubmitting(true);

    try {
      const response = await CreateSubDepartment(subDepartmentRequest);
      if (response.status === 200) {
        dispatch(
          successNotification({
            header: "Sub-Department Created",
            message: `Sub-department "${subDepartment.Name}" was successfully created!`,
          })
        );
        history.push("/admin?tab=organization&subtab=subdepartments");
      } else if (response.status === 204) {
        dispatch(
          errorNotification({
            header: "Sub-Department Exists",
            message: "Sub-department already exists!",
          })
        );
      }
    } catch (error) {
      console.error("Failed to create sub-department: ", error);
      dispatch(
        errorNotification({
          header: "Error",
          message: `Something went wrong. Please try again!`,
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    history.push("/admin?tab=organization&subtab=subdepartments");
  };

  return isLoading ? (
    <div className="pulse-loader">
      <PulseLoader />
    </div>
  ) : (
    <SubDepartmentLayout
      subDepartment={subDepartment}
      setSubDepartment={setSubDepartment}
      selectedValue={selectedValue}
      setSelectedValue={setSelectedValue}
      isSubmitting={isSubmitting}
      handleSave={handleSave}
      handleCancel={handleCancel}
      errors={errors}
      setErrors={setErrors}
      isEdit={false}
    />
  );
};

export default AdminSubDepartmentCreatePanel;
